import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  useQueryClient,
} from 'react-query';
import { Tracking } from '@app/analytics';
import { queryCreate, queryGet } from './fetch';
import { Participation } from './participation';
import {
  CreateFormResponse,
  useApplicationAnswersQuery,
  usePrefetchFormResponse,
} from './formAnswer';
import { useIsAdmin } from './auth';

export enum CriterionStatusEnum {
  QUALIFY = 'QUALIFIED',
  DISQUALIFY = 'DISQUALIFIED',
  IGNORE = 'IGNORED',
  WAITING = 'WAITING',
  OUT_OF_QUOTA = 'OUT_OF_QUOTA',
}

export interface CriterionStatus {
  id: string;
  fieldRef: string;
  status: CriterionStatusEnum;
  item?: string;
}

export enum ApplicationStatus {
  APPLYING = 'applying',
  APPLIED = 'applied',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TO_BE_VALIDATED = 'to_be_validated',
}

export interface Application {
  id: string;
  trackingId: string;
  formResponseId: string;
  recruitmentId: string;
  sessionId: string;
  sessionLinkId?: string;
  participantId?: string;
  status: ApplicationStatus;
  criteriaStatus: CriterionStatus[];
}

export enum StrategyType {
  FRAUD_DETECTION = 'FRAUD_DETECTION',
  CRITERIA_VALIDATION = 'CRITERIA_VALIDATION',
  BAN = 'BAN',
}

export enum ValidationResult {
  VALID = 'VALID',
  SKIP = 'SKIP',
  BLOCK = 'BLOCK',
  AUTOMATICALLY_ACCEPT = 'AUTOMATICALLY_ACCEPT',
}

export interface ValidationStrategyResult {
  id: string;
  applicationId: string;
  strategy: string;
  strategyType: StrategyType;
  checkedValue?: string;
  shortText: string;
  result: ValidationResult;
  score?: number;
  studyLocation?: { link: string };
  details?: any;
  createdAt: Date;
}

interface ApplyData {
  recruitmentId: string;
  sessionId: string;
  trackingId?: string;
  participantId?: string;
  sessionLinkId?: string;
  tracking?: Tracking;
  formResponse?: CreateFormResponse;
  sync?: boolean;
  fingerprint?: string;
  timezone?: string;
}

export const useApply = () => {
  return useMutation<Application, unknown, ApplyData>(
    queryCreate(`/v2/applications/`),
  );
};

export const useApplication = (
  id?: string,
  options?: UseQueryOptions<Application>,
) => {
  return useQuery<Application>(
    ['applications', id],
    queryGet(`/v2/applications/${id}`),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const useApplicationAnswerStatus = (id?: string) => {
  return useQuery<CriterionStatus[]>(
    ['applications', id, 'form-answers-status'],
    queryGet(`/v2/applications/${id}/form-answers-status`),
    {
      enabled: !!id,
    },
  );
};

export const useRecruitmentParticipantApplications = ({
  recruitmentId,
  participantId,
}: {
  recruitmentId?: string;
  participantId?: string;
}) => {
  return useQuery<Application[]>(
    [
      'recruitments',
      recruitmentId,
      'participants',
      participantId,
      'applications',
    ],
    queryGet(
      `/v2/recruitments/${recruitmentId}/participants/${participantId}/applications`,
    ),
    {
      enabled: !!recruitmentId && !!participantId,
    },
  );
};

export const useValidationStrategyResultsByApplicationId = (id?: string) => {
  const isAdmin = useIsAdmin();
  return useQuery<ValidationStrategyResult[]>(
    ['application', id, 'validation-strategy-results'],
    queryGet(`/v2/applications/${id}/validation-strategy-results`),
    {
      enabled: !!id && isAdmin,
    },
  );
};

export const usePrefetchParticipationResponse = () => {
  const queryClient = useQueryClient();
  const applicationAnswersQuery = useApplicationAnswersQuery();

  return useCallback(
    async (participationId: string, applicationId?: string) => {
      const cachedParticipation = queryClient.getQueryData<Participation>([
        'participations',
        participationId,
      ]);
      if (!applicationId) {
        return;
      }
      const cachedFormResponse = queryClient.getQueryData([
        'applications',
        applicationId,
        'answers-texts',
      ]);

      if (cachedParticipation && cachedFormResponse) {
        return;
      }

      const promises: Promise<any>[] = [];
      if (!cachedParticipation) {
        promises.push(
          queryGet(`/participations/${participationId}`)().then(
            (participation) => {
              if (participation) {
                queryClient.setQueryData(
                  ['participations', participationId],
                  participation,
                );
              }
              return participation;
            },
          ),
        );
      }

      if (!cachedFormResponse) {
        promises.push(
          queryClient.prefetchQuery(
            ['applications', applicationId, 'answers-texts'],
            applicationAnswersQuery(applicationId),
          ),
        );
      }

      await Promise.all(promises);
    },
    [queryClient, applicationAnswersQuery],
  );
};
